// @archigraph r8DljQiU
// Documentation page for archigraph.ai

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Container } from '../components/ui/Container';

const sections = [
  { id: 'overview', label: 'Overview' },
  { id: 'why', label: 'Why ArchiGraph' },
  { id: 'how-it-works', label: 'How It Works' },
  { id: 'format', label: 'The Format' },
  { id: 'ai-agents', label: 'AI Agents' },
  { id: 'workflow', label: 'Workflow' },
  { id: 'getting-started', label: 'Getting Started' },
] as const;

function CodeBlock({ children, filename }: { children: string; filename?: string }) {
  return (
    <div className="border border-border bg-bg-white my-6">
      {filename && (
        <div className="flex items-center gap-2 px-4 py-2 border-b border-border">
          <div className="w-2 h-2 rounded-full bg-error/50" />
          <div className="w-2 h-2 rounded-full bg-warning/50" />
          <div className="w-2 h-2 rounded-full bg-primary/50" />
          <span className="text-xs text-text-lighter ml-2">{filename}</span>
        </div>
      )}
      <pre className="p-6 text-xs md:text-sm text-text-light overflow-x-auto leading-relaxed">
        <code>{children}</code>
      </pre>
    </div>
  );
}

function Callout({ children }: { children: React.ReactNode }) {
  return (
    <div className="border-l-2 border-primary pl-6 my-8">
      {children}
    </div>
  );
}

export function DocsPage() {
  const [activeSection, setActiveSection] = useState('overview');

  const handleClick = (id: string) => {
    setActiveSection(id);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="py-16">
      <Container>
        <div className="flex gap-16">
          {/* Sidebar */}
          <nav className="hidden lg:block w-48 shrink-0 sticky top-20 self-start">
            <p className="text-xs uppercase tracking-widest text-primary mb-4 font-bold">
              // docs
            </p>
            <ul className="space-y-1">
              {sections.map((s) => (
                <li key={s.id}>
                  <button
                    onClick={() => handleClick(s.id)}
                    className={`block w-full text-left text-xs py-1.5 transition-colors ${
                      activeSection === s.id
                        ? 'text-primary font-bold'
                        : 'text-text-lighter hover:text-text-light'
                    }`}
                  >
                    {s.label}
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          {/* Content */}
          <div className="flex-1 min-w-0 max-w-3xl">
            <p className="text-xs uppercase tracking-widest text-primary mb-4 font-bold lg:hidden">
              // docs
            </p>
            <h1 className="text-3xl md:text-4xl font-bold text-text mb-2">
              Documentation
            </h1>
            <p className="text-sm text-text-lighter mb-16">
              Architecture-as-code for AI-powered development
            </p>

            {/* Overview */}
            <section id="overview" className="mb-20 scroll-mt-20">
              <h2 className="text-sm font-bold text-text uppercase tracking-wider mb-4">
                Overview
              </h2>
              <p className="text-sm text-text-light leading-relaxed mb-4">
                ArchiGraph is a format and desktop application for describing software architecture as a directed graph.
                You define your system — services, databases, APIs, queues, boundaries — as nodes and edges in YAML.
                AI coding agents read this graph and implement each component with full knowledge of how it connects to
                everything else.
              </p>
              <p className="text-sm text-text-light leading-relaxed">
                The result: AI agents that don't just write isolated files, but build entire systems where every service
                knows its dependencies, every API knows its consumers, and every database knows its access patterns.
              </p>
            </section>

            {/* Why ArchiGraph */}
            <section id="why" className="mb-20 scroll-mt-20">
              <h2 className="text-sm font-bold text-text uppercase tracking-wider mb-4">
                Why ArchiGraph
              </h2>
              <p className="text-sm text-text-light leading-relaxed mb-6">
                Large applications fail for one reason: components are built in isolation without understanding the whole.
                A developer working on the auth service doesn't know how the notification service consumes user events.
                An AI agent generating a database schema doesn't know which services will query it and how.
              </p>
              <p className="text-sm text-text-light leading-relaxed mb-6">
                ArchiGraph solves this by giving every agent — human or AI — a single source of truth for the entire system.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border mb-6">
                <div className="bg-bg p-6">
                  <h3 className="text-xs font-bold text-text uppercase tracking-wider mb-2">
                    Without ArchiGraph
                  </h3>
                  <ul className="space-y-2">
                    {[
                      'AI generates code that compiles but doesn\'t integrate',
                      'Architecture docs are stale before the PR merges',
                      'Each microservice is an island',
                      'New developers take weeks to understand the system',
                      'Refactoring is guesswork about what might break',
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-2 text-xs text-text-lighter">
                        <span className="text-error mt-0.5 shrink-0">&times;</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-bg p-6">
                  <h3 className="text-xs font-bold text-text uppercase tracking-wider mb-2">
                    With ArchiGraph
                  </h3>
                  <ul className="space-y-2">
                    {[
                      'AI reads the full architecture before writing a line',
                      'The graph updates as you build — always current',
                      'Every service knows its neighbors and contracts',
                      'New developers read one file and see the whole system',
                      'Impact analysis is a graph query away',
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-2 text-xs text-text-lighter">
                        <span className="text-primary mt-0.5 shrink-0">+</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <Callout>
                <p className="text-sm text-text-light leading-relaxed">
                  ArchiGraph is not a diagramming tool. It's a <strong className="text-text">machine-readable system specification</strong> that
                  happens to produce beautiful diagrams. The graph is the product — the diagram is a view.
                </p>
              </Callout>
            </section>

            {/* How It Works */}
            <section id="how-it-works" className="mb-20 scroll-mt-20">
              <h2 className="text-sm font-bold text-text uppercase tracking-wider mb-4">
                How It Works
              </h2>
              <p className="text-sm text-text-light leading-relaxed mb-8">
                An ArchiGraph workspace is a folder with two files and an implementations directory.
              </p>

              <CodeBlock filename="workspace/">{`├── schema.yaml        # Vocabulary: layers, node kinds, edge kinds, fields
├── archigraph.yaml    # Architecture: nodes, edges, views, rules
└── implementations/   # One folder per implementable component
    ├── svc.api/
    │   ├── AGENTS.md   # Auto-generated implementation requirements
    │   └── src/        # Your code
    ├── svc.auth/
    │   ├── AGENTS.md
    │   └── src/
    └── ...`}</CodeBlock>

              <div className="space-y-6">
                <div className="flex gap-4">
                  <span className="text-primary font-bold text-sm shrink-0 w-6">01</span>
                  <div>
                    <h3 className="text-xs font-bold text-text uppercase tracking-wider mb-1">Define your schema</h3>
                    <p className="text-xs text-text-lighter leading-relaxed">
                      The schema defines the vocabulary for your organization. What kinds of nodes exist (services, databases,
                      queues, UIs), what relationships are valid (calls, reads, writes, contains), and what metadata each kind carries.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <span className="text-primary font-bold text-sm shrink-0 w-6">02</span>
                  <div>
                    <h3 className="text-xs font-bold text-text uppercase tracking-wider mb-1">Draw your architecture</h3>
                    <p className="text-xs text-text-lighter leading-relaxed">
                      Using the visual editor or YAML directly, add nodes and connect them with edges. Each node carries
                      rich metadata — ports, protocols, auth mechanisms, database schemas, environment variables.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <span className="text-primary font-bold text-sm shrink-0 w-6">03</span>
                  <div>
                    <h3 className="text-xs font-bold text-text uppercase tracking-wider mb-1">Build with agents</h3>
                    <p className="text-xs text-text-lighter leading-relaxed">
                      ArchiGraph generates an AGENTS.md for each implementable component, listing exactly what to build:
                      its edges, its neighbors' interfaces, its data contracts. AI agents read this and generate production code.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <span className="text-primary font-bold text-sm shrink-0 w-6">04</span>
                  <div>
                    <h3 className="text-xs font-bold text-text uppercase tracking-wider mb-1">Keep it in sync</h3>
                    <p className="text-xs text-text-lighter leading-relaxed">
                      As you implement, the archigraph updates with you. New dependencies get new nodes. Concrete decisions
                      update the metadata. The architecture and code stay in lockstep.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* The Format */}
            <section id="format" className="mb-20 scroll-mt-20">
              <h2 className="text-sm font-bold text-text uppercase tracking-wider mb-4">
                The Format
              </h2>
              <p className="text-sm text-text-light leading-relaxed mb-4">
                An ArchiGraph is a directed graph described in YAML. Nodes are architectural elements.
                Edges are relationships between them. Extension fields (<code className="text-primary">x.*</code>) carry
                implementation-specific metadata.
              </p>

              <h3 className="text-xs font-bold text-text uppercase tracking-wider mt-8 mb-3">Nodes</h3>
              <p className="text-xs text-text-lighter leading-relaxed mb-3">
                Every architectural element is a node — services, databases, APIs, UIs, queues, boundaries, actors.
              </p>
              <CodeBlock filename="archigraph.yaml">{`nodes:
  - id: svc.api
    kind: container
    layer: runtime
    name: API Server
    x:
      service.language: TypeScript
      service.framework: Fastify
      container.port: 3000
      container.healthCheck: /health

  - id: db.users
    kind: datastore
    layer: data
    name: Users DB
    type: DynamoDB
    x:
      db.tables: "users (pk: userId, gsi: email)"
      security.encryptionAtRest: true`}</CodeBlock>

              <h3 className="text-xs font-bold text-text uppercase tracking-wider mt-8 mb-3">Edges</h3>
              <p className="text-xs text-text-lighter leading-relaxed mb-3">
                Edges describe how components connect. Each edge has a kind (reads, writes, calls, contains, deploys),
                a source, a target, and optional metadata.
              </p>
              <CodeBlock filename="archigraph.yaml">{`edges:
  - kind: reads
    from: svc.api
    to: db.users
    layer: data
    x:
      protocol: DynamoDB SDK
      auth.mechanism: IAM role

  - kind: calls
    from: svc.api
    to: svc.auth
    layer: runtime
    x:
      protocol: gRPC
      auth.mechanism: mTLS`}</CodeBlock>

              <h3 className="text-xs font-bold text-text uppercase tracking-wider mt-8 mb-3">Schema</h3>
              <p className="text-xs text-text-lighter leading-relaxed mb-3">
                The schema defines what's valid in your workspace — which node kinds exist, what fields they carry,
                which edge kinds connect which nodes. It's the type system for your architecture.
              </p>
              <CodeBlock filename="schema.yaml">{`layers:
  - id: product
    name: Product
  - id: runtime
    name: Runtime
  - id: data
    name: Data

nodeKinds:
  - id: container
    name: Service Container
    shape: rectangle
    color: "#3B82F6"
    implementable: true
  - id: datastore
    name: Data Store
    shape: cylinder
    color: "#F59E0B"

edgeKinds:
  - id: reads
    name: Reads from
  - id: writes
    name: Writes to
  - id: calls
    name: Calls`}</CodeBlock>
            </section>

            {/* AI Agents */}
            <section id="ai-agents" className="mb-20 scroll-mt-20">
              <h2 className="text-sm font-bold text-text uppercase tracking-wider mb-4">
                AI Agents
              </h2>
              <p className="text-sm text-text-light leading-relaxed mb-6">
                ArchiGraph was built for AI. When an AI coding agent enters an implementation folder,
                it reads the generated AGENTS.md and immediately knows:
              </p>

              <ul className="space-y-3 mb-8">
                {[
                  ['What to build', 'The component\'s purpose, kind, and all its metadata'],
                  ['Who it talks to', 'Every edge — which services it calls, what databases it reads, what events it publishes'],
                  ['What contracts to honor', 'The interfaces it must implement, the schemas it must conform to'],
                  ['What its neighbors expect', 'How other services will call it, what auth they\'ll use, what data format they expect'],
                  ['What already exists', 'Which sibling implementation folders contain related code it can reference'],
                ].map(([title, desc]) => (
                  <li key={title} className="flex items-start gap-3 text-xs text-text-lighter">
                    <span className="text-primary mt-0.5 shrink-0">+</span>
                    <span>
                      <strong className="text-text">{title}</strong> — {desc}
                    </span>
                  </li>
                ))}
              </ul>

              <Callout>
                <p className="text-sm text-text-light leading-relaxed mb-2">
                  <strong className="text-text">The feedback loop is mandatory.</strong>
                </p>
                <p className="text-xs text-text-lighter leading-relaxed">
                  As agents implement code, they update the archigraph with concrete details — chosen ports, database
                  table structures, environment variables, new dependencies. The architecture evolves with the code,
                  not separately from it. Code-to-architecture traceability is maintained via <code className="text-primary">@archigraph</code> comments
                  in the source.
                </p>
              </Callout>

              <h3 className="text-xs font-bold text-text uppercase tracking-wider mt-8 mb-3">Compatible Agents</h3>
              <p className="text-xs text-text-lighter leading-relaxed mb-3">
                ArchiGraph works with any AI coding agent that can read files. Agents that work particularly well:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-border">
                {[
                  ['Claude Code', 'Reads CLAUDE.md and AGENTS.md automatically. Full architecture context in every session.'],
                  ['Cursor / Windsurf', 'Add archigraph.yaml and AGENTS.md to context. The agent sees the full graph.'],
                  ['Custom agents', 'Parse the YAML programmatically. Build tools that understand your architecture.'],
                ].map(([name, desc]) => (
                  <div key={name} className="bg-bg p-6">
                    <h4 className="text-xs font-bold text-text uppercase tracking-wider mb-2">{name}</h4>
                    <p className="text-xs text-text-lighter leading-relaxed">{desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Workflow */}
            <section id="workflow" className="mb-20 scroll-mt-20">
              <h2 className="text-sm font-bold text-text uppercase tracking-wider mb-4">
                Workflow
              </h2>
              <p className="text-sm text-text-light leading-relaxed mb-8">
                A typical workflow for building a large application with ArchiGraph.
              </p>

              <div className="space-y-8">
                <div>
                  <h3 className="text-xs font-bold text-text uppercase tracking-wider mb-2">
                    1. Architect
                  </h3>
                  <p className="text-xs text-text-lighter leading-relaxed mb-3">
                    Open ArchiGraph and draw your system. Start high-level — products, actors, key services. Then zoom in:
                    add databases, APIs, queues, auth boundaries. Fill in the extension fields as you make decisions.
                  </p>
                  <p className="text-xs text-text-lighter leading-relaxed">
                    Don't over-specify. The archigraph grows with your understanding. Start with the shape of the system
                    and add detail as you go.
                  </p>
                </div>

                <div>
                  <h3 className="text-xs font-bold text-text uppercase tracking-wider mb-2">
                    2. Generate
                  </h3>
                  <p className="text-xs text-text-lighter leading-relaxed">
                    ArchiGraph creates an implementation folder for each implementable node. Inside each folder, AGENTS.md
                    contains everything an agent needs to build that component — extracted from the graph's edges, neighbors,
                    and metadata. The agent doesn't need to understand the whole system, just its local neighborhood.
                  </p>
                </div>

                <div>
                  <h3 className="text-xs font-bold text-text uppercase tracking-wider mb-2">
                    3. Implement
                  </h3>
                  <p className="text-xs text-text-lighter leading-relaxed">
                    Point your AI agent at an implementation folder. It reads AGENTS.md, understands the component's role,
                    and generates code that fits into the larger system. Because it knows the edges, it generates correct
                    client calls, proper schemas, and matching interfaces.
                  </p>
                </div>

                <div>
                  <h3 className="text-xs font-bold text-text uppercase tracking-wider mb-2">
                    4. Evolve
                  </h3>
                  <p className="text-xs text-text-lighter leading-relaxed">
                    As you build, update the archigraph. New database table? Add a node. Chose gRPC over REST? Update the edge.
                    The architecture and code stay synchronized. When you come back in six months, the archigraph tells you
                    exactly what the system looks like — not what it looked like when someone last updated the wiki.
                  </p>
                </div>
              </div>
            </section>

            {/* Getting Started */}
            <section id="getting-started" className="mb-20 scroll-mt-20">
              <h2 className="text-sm font-bold text-text uppercase tracking-wider mb-4">
                Getting Started
              </h2>

              <div className="space-y-6">
                <div className="flex gap-4">
                  <span className="text-primary font-bold text-sm shrink-0 w-6">01</span>
                  <div>
                    <h3 className="text-xs font-bold text-text uppercase tracking-wider mb-1">Download ArchiGraph</h3>
                    <p className="text-xs text-text-lighter leading-relaxed">
                      Get the desktop app for macOS from the{' '}
                      <Link to="/download" className="text-primary hover:text-primary-light">download page</Link>.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <span className="text-primary font-bold text-sm shrink-0 w-6">02</span>
                  <div>
                    <h3 className="text-xs font-bold text-text uppercase tracking-wider mb-1">Create a workspace</h3>
                    <p className="text-xs text-text-lighter leading-relaxed">
                      Open the app, click "New Workspace", and choose a folder. Select a schema template or start blank.
                      ArchiGraph creates the schema.yaml and archigraph.yaml files for you.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <span className="text-primary font-bold text-sm shrink-0 w-6">03</span>
                  <div>
                    <h3 className="text-xs font-bold text-text uppercase tracking-wider mb-1">Add your first nodes</h3>
                    <p className="text-xs text-text-lighter leading-relaxed">
                      Add a few nodes — maybe a frontend, a backend API, and a database. Connect them with edges.
                      Fill in the metadata that matters: languages, frameworks, ports, protocols.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <span className="text-primary font-bold text-sm shrink-0 w-6">04</span>
                  <div>
                    <h3 className="text-xs font-bold text-text uppercase tracking-wider mb-1">Build implementations</h3>
                    <p className="text-xs text-text-lighter leading-relaxed">
                      Use the built-in AI chat or open an implementation folder in your editor with Claude Code, Cursor,
                      or any AI coding agent. The AGENTS.md file gives the agent everything it needs.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* CTA */}
            <section className="border-t border-border pt-12">
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/download"
                  className="inline-flex items-center justify-center rounded-none font-bold uppercase tracking-wider bg-primary text-bg hover:bg-primary-light px-8 py-3 text-sm transition-all min-h-[44px]"
                >
                  Download
                </Link>
                <Link
                  to="/signup"
                  className="inline-flex items-center justify-center rounded-none font-medium uppercase tracking-wider border border-border text-text-light hover:text-text hover:border-text-lighter px-8 py-3 text-sm transition-all min-h-[44px]"
                >
                  Create Account
                </Link>
              </div>
            </section>
          </div>
        </div>
      </Container>
    </div>
  );
}
